import type {
  PredefinedDrillConfig,
  PredefinedDrillTemplate,
} from "@acepoker/contract";
import {
  getRandomElement,
  getRandomInt,
  takeRandomElements,
} from "@acepoker/utils";
import assert from "node:assert";
import { drills } from ".";
import type { DrillConfigField } from "./types";

type Context = Record<string, string | Array<string>>;

export function getDefaultValue(field: DrillConfigField, context: Context) {
  if (field.options.type === "static") {
    const value = field.options.values[0]!.value;
    return field.multiple ? [value] : value;
  } else {
    const firstSatisfiedRule = field.options.rules.find((rule) =>
      isRuleSatisfied(rule.condition, context),
    );
    if (!firstSatisfiedRule) {
      throw new Error(`No rule satisfied for field ${field.field}`);
    }
    const value = firstSatisfiedRule.values[0]?.value;
    if (!value) {
      throw new Error(`No value found for field ${field.field}`);
    }
    return field.multiple ? firstSatisfiedRule.required ?? [value] : value;
  }
}

export function getRandomValue(
  field: DrillConfigField,
  context: Context,
): string | string[] {
  if (field.options.type === "static") {
    const values = field.multiple
      ? takeRandomElements(
          field.options.values,
          getRandomInt(1, field.max ?? field.options.values.length),
        ).map((value) => value.value)
      : getRandomElement(field.options.values)?.value;
    if (!values) {
      throw new Error("No value found");
    }
    return values;
  } else {
    const satisfyingRules = field.options.rules.filter((rule) =>
      isRuleSatisfied(rule.condition, context),
    );
    if (!satisfyingRules) {
      throw new Error("No satisfying rules found");
    }
    const randomSatisfyingRule = getRandomElement(satisfyingRules);
    if (!randomSatisfyingRule) {
      throw new Error("No satisfying rule found");
    }
    const values = field.multiple
      ? takeRandomElements(
          randomSatisfyingRule.values,
          getRandomInt(
            1,
            field.max ??
              randomSatisfyingRule.max ??
              randomSatisfyingRule.values.length,
          ),
        ).map((value) => value.value)
      : getRandomElement(randomSatisfyingRule.values)?.value;
    if (!values) {
      throw new Error("No value found");
    }
    return values;
  }
}

export function isRuleSatisfied(
  condition: Record<string, string | Array<string>>,
  context: Context,
) {
  return Object.entries(condition).every(([key, value]) => {
    if (!context[key]) {
      return false;
    }
    if (typeof value === "string" && context[key] !== value) {
      return false;
    }

    if (Array.isArray(value) && !Array.isArray(context[key])) {
      return false;
    }

    if (
      Array.isArray(value) &&
      Array.isArray(context[key]) &&
      [...value].sort().join(",") !== [...context[key]].sort().join(",")
    ) {
      return false;
    }

    return true;
  });
}

export function getNextFields(field: DrillConfigField, context: Context) {
  if (field.options.type === "static") {
    const selectedOption = field.options.values.find(
      (option) => option.value === context[field.field],
    );
    return selectedOption?.children_fields ?? [];
  } else if (field.options.type === "dynamic") {
    // We have no dynamic fields setting dependencies for now
    return [];
  }
  return [];
}

export function getPossibleOptions(field: DrillConfigField, context: Context) {
  return field.options.type === "static"
    ? field.options.values.map(({ value, title, hidden }) => ({
        value,
        title,
        hidden,
      }))
    : (
        field.options.rules.find((rule) =>
          isRuleSatisfied(rule.condition, context),
        )?.values ?? []
      ).map(({ value, title, required }) => ({
        value,
        title,
        required,
        hidden: false,
      }));
}

export function initConfigWithDefaults(
  drillsConfig: DrillConfigField,
  previousConfig: Context,
  config: Context,
  getValue: (
    field: DrillConfigField,
    context: Context,
  ) => string | string[] = getDefaultValue,
) {
  const newConfig: Context = {};
  const configFields = [drillsConfig];
  while (configFields.length > 0) {
    const field = configFields.shift();
    if (!field) {
      throw new Error("Unexpected error: field is empty");
    }
    const newPossibleOptions = getPossibleOptions(field, newConfig);
    const isStreetNotUpdated = previousConfig.street === newConfig.street;
    if (field.multiple === true) {
      const availablePreviousOptions = Array.isArray(config[field.field])
        ? (config[field.field] as string[]).filter((option) =>
            newPossibleOptions.some((p) => p.value === option),
          )
        : [];

      newConfig[field.field] =
        field.field === "street"
          ? availablePreviousOptions.length > 0
            ? availablePreviousOptions
            : getValue(field, newConfig)
          : isStreetNotUpdated && availablePreviousOptions.length > 0
            ? availablePreviousOptions
            : getValue(field, newConfig);
    } else {
      const oldValue = config[field.field];
      newConfig[field.field] =
        field.field === "street"
          ? oldValue &&
            newPossibleOptions.some((option) => option.value === oldValue)
            ? oldValue
            : getValue(field, newConfig)
          : isStreetNotUpdated &&
              oldValue &&
              newPossibleOptions.some((option) => option.value === oldValue)
            ? oldValue
            : getValue(field, newConfig);
    }
    configFields.push(...getNextFields(field, newConfig));
  }
  return newConfig;
}

export const predefinedDrillConfigToContext = (
  config: PredefinedDrillTemplate["config"],
): Context => {
  const processConfig = (config: PredefinedDrillConfig) =>
    Object.fromEntries(
      Object.entries(config).map(([key, value]) => {
        const newValue =
          typeof value === "object" && "oneOf" in value
            ? getRandomElement(value.oneOf)
            : value;
        assert(newValue !== undefined);
        return [key, newValue];
      }),
    );
  const processedConfig = (() => {
    if (Array.isArray(config)) {
      const singleConfig = getRandomElement(config);
      assert(singleConfig !== undefined);
      return processConfig(singleConfig);
    } else {
      return processConfig(config);
    }
  })();
  return initConfigWithDefaults(
    drills.config,
    processedConfig,
    processedConfig,
    getRandomValue,
  );
};
